import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./Pagination.scss";
import { useTranslation } from "react-i18next";
import FormSelect, { IOption } from "../Forms/FormInput/FormSelect/FormSelect";

interface IPagination {
  options: IOption[];
  limit: number;
  count: number;
  skip: number;
  setLimit: (limit: number) => void;
  setSkip: (skip: number) => void;
}

const Pagination: FC<IPagination> = ({
  limit,
  count,
  skip,
  setLimit,
  setSkip,
  options,
}) => {
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);

  useEffect(() => {
    const totalPages = Math.ceil(count / limit);
    const currentPage = Math.floor(skip / limit) + 1;
    setTotalPage(totalPages);
    setPage(currentPage);
  }, [count, limit, skip]);

  const goForward = useCallback(() => {
    if (page < totalPage) {
      const nextPage = page + 1;
      const newSkip = Number(skip) + Number(limit);
      setPage(nextPage);
      setSkip(newSkip);
      const currentPage = Math.floor(skip / limit) + 1;
      setPage(currentPage);
    }
  }, [limit, page, setSkip, skip, totalPage]);

  const goBack = useCallback(() => {
    if (page > 1) {
      const prevPage = page - 1;
      const newSkip = Number(skip) - Number(limit);
      setPage(prevPage);
      setSkip(newSkip);
    }
  }, [limit, page, setSkip, skip]);

  const goEnd = useCallback(() => {
    if (page === totalPage) return;
    const lastPage = totalPage;
    const newSkip = (lastPage - 1) * limit;
    setPage(lastPage);
    setSkip(newSkip);
  }, [limit, page, setSkip, totalPage]);

  const goStart = useCallback(() => {
    setPage(1);
    setSkip(0);
  }, [setSkip]);

  const { t } = useTranslation();
  const paginationComponent = useMemo(() => {
    return (
      <Fragment>
        <button type="button" disabled={page === 1} onClick={() => goStart()}>
          <i className="icon icon-chevrons-left icon-page" />
        </button>
        <button type="button" disabled={page === 1} onClick={() => goBack()}>
          <i className="icon icon-ch-left icon-page" />
        </button>
        <button
          type="button"
          disabled={page === totalPage}
          onClick={() => goForward()}
        >
          <i className="icon icon-ch-right icon-page" />
        </button>
        <button
          type="button"
          disabled={page === totalPage}
          onClick={() => goEnd()}
        >
          <i className="icon icon-chevrons-right icon-page" />
        </button>
      </Fragment>
    );
  }, [goBack, goEnd, goForward, goStart, page, totalPage]);

  return (
    <div className="container-pagination">
      <span className="pages-label pe-3">{`${t("pagination.selectLabel")}`}</span>
      {options && (
        <FormSelect
          wrapperClass="select-pagination"
          label=""
          placeholder=""
          defaultValue={String(limit)}
          options={options}
          transparent
          onChange={(selcted: any) => {
            setLimit(selcted);
          }}
        />
      )}
      <span className="pages-label">
        {page} {t("pagination.of")} {totalPage}
      </span>
      {paginationComponent}
    </div>
  );
};

export default Pagination;
